import React from "react";
import { useNavigate } from "react-router-dom";
import "./Services.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSitemap,
  faLaptopCode,
  faBullseye,
  faMagnifyingGlassChart,
  faArrowsRotate,
  faUserGroup,
} from "@fortawesome/free-solid-svg-icons";

const Services = () => {
  const navigate = useNavigate();

  const services = [
    {
      id: "go-to-market-strategy",
      icon: faSitemap,
      title: "Go-to-Market & Funnel Strategy",
      desc:
        "Clarify your offer, audience, and funnel. We map the journey from first touch to closed deal.",
      tags: ["Positioning", "Funnel Mapping", "Offer Design"],
    },
    {
      id: "websites-funnels",
      icon: faLaptopCode,
      title: "Websites & Landing Funnels",
      desc:
        "Fast, responsive websites and landing pages designed to convert—built on React or WordPress.",
      tags: ["Marketing Sites", "Landing Pages", "CRO UX"],
    },
    {
      id: "paid-growth",
      icon: faBullseye,
      title: "Paid Growth Engines",
      desc:
        "Acquisition systems across Google & Meta with clear targets, testing roadmaps, and ROAS tracking.",
      tags: ["Google Ads", "Meta Ads", "Retargeting"],
    },
    {
      id: "seo-content",
      icon: faMagnifyingGlassChart,
      title: "SEO & Content Systems",
      desc:
        "Compounding organic traffic with technical SEO, content hubs, and pages built around search intent.",
      tags: ["Technical SEO", "Content Hubs", "Schema"],
    },
    {
      id: "automation-crm",
      icon: faArrowsRotate,
      title: "Automation & CRM Flows",
      desc:
        "Make every lead count. We plug your forms, ads, and site into CRM, email, and reporting dashboards.",
      tags: ["CRM", "Email Journeys", "Dashboards"],
    },
    {
      id: "growth-partners",
      icon: faUserGroup,
      title: "Ongoing Growth Partnership",
      desc:
        "A cross-functional pod plugged into your team—owning experiments, reporting and continuous improvements.",
      tags: ["Pods", "Sprints", "Reporting"],
    },
  ];

  const open = (id) => navigate(`/services/${id}`);
  const onKey = (e, id) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      open(id);
    }
  };

  return (
    <section className="svc2" aria-label="Services">
      <div className="svc2-wrap">
        {/* Header */}
        <header className="svc2-head">
          <p className="svc2-kicker">How we can help</p>
          <h1>From idea to live, with one team</h1>
          <p className="svc2-sub">
            Whether you’re validating a new offer or scaling an existing one, we plug in where
            it hurts most—strategy, website, or growth—so you’re not juggling five different vendors.
          </p>
        </header>

        {/* Grid */}
        <div className="svc2-grid" role="list">
          {services.map((s) => (
            <article
              key={s.id}
              role="listitem"
              className="svc2-card"
              tabIndex={0}
              onClick={() => open(s.id)}
              onKeyDown={(e) => onKey(e, s.id)}
              aria-label={`${s.title} — ${s.desc}`}
            >
              <div className="svc2-top">
                <div className="svc2-icon">
                  <FontAwesomeIcon icon={s.icon} />
                </div>
              </div>

              <h3 className="svc2-title">{s.title}</h3>
              <p className="svc2-desc">{s.desc}</p>

              <div className="svc2-tags">
                {s.tags.map((t) => (
                  <span key={t} className="tag">
                    {t}
                  </span>
                ))}
              </div>

              <div className="svc2-cta" aria-hidden="true">
                View what’s included →
              </div>
            </article>
          ))}
        </div>

        {/* Mini KPIs */}
        <div className="svc2-stats" role="list">
          <div className="stat" role="listitem">
            <div className="k">7–21 days</div>
            <div className="d">typical time to first launch</div>
          </div>
          <div className="stat" role="listitem">
            <div className="k">30%+</div>
            <div className="d">average uplift in key conversion</div>
          </div>
          <div className="stat" role="listitem">
            <div className="k">4.9 / 5</div>
            <div className="d">average client satisfaction score</div>
          </div>
        </div>

        {/* CTA */}
        <div className="svc2-cta-row">
          <a className="svc2-btn svc2-btn-primary" href="/contact" aria-label="Start your project">
            Start a project brief
          </a>
          <a className="svc2-btn svc2-btn-ghost" href="/process" aria-label="See how we work">
            See how we work
          </a>
        </div>

        {/* FAQ */}
        <div className="svc2-faq">
          <details>
            <summary>What does a typical engagement look like?</summary>
            <p>
              Most projects start with a discovery sprint, followed by a 60–90 day build & growth
              phase. You get a clear roadmap, timelines, and owners for each initiative.
            </p>
          </details>
          <details>
            <summary>Can you work with our existing site or team?</summary>
            <p>
              Yes. We can improve your current stack, collaborate with in-house devs and marketers,
              or own the full scope if you prefer a plug-and-play team.
            </p>
          </details>
          <details>
            <summary>How do you price your services?</summary>
            <p>
              Fixed-fee for clearly scoped builds (like a site or funnel), and monthly retainers for
              ongoing growth work. Everything is quoted upfront—no surprise line items.
            </p>
          </details>
        </div>
      </div>
    </section>
  );
};

export default Services;
